import { PASSWORD_MIN_LENGTH } from './passwordPolicy'

/**
 * LOT-MDP-8 : la VALIDATION (`isPasswordValid`, `getPasswordErrors`) applique
 * la règle produit unique — 8 caractères minimum, rien d'autre. La jauge de
 * force (`calculatePasswordStrength`) reste purement INDICATIVE : elle note
 * la robustesse (longueur, variété de caractères) mais ne bloque plus rien.
 */
export interface PasswordStrength {
  score: number // 0-4
  label: 'Très faible' | 'Faible' | 'Moyen' | 'Fort' | 'Très fort'
  color: string
  percentage: number
  requirements: {
    minLength: boolean
    hasUppercase: boolean
    hasLowercase: boolean
    hasNumber: boolean
    hasSpecial: boolean
  }
}

export function calculatePasswordStrength(password: string): PasswordStrength {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSpecial: /[@$!%*?&]/.test(password),
  }

  // Count met requirements
  const metCount = Object.values(requirements).filter(Boolean).length

  // Calculate score (0-4)
  let score = 0
  if (password.length >= 8) score++
  if (password.length >= 12) score++
  if (metCount >= 3) score++
  if (metCount >= 4) score++
  if (metCount === 5 && password.length >= 12) score = 4

  // Determine label and color
  let label: PasswordStrength['label']
  let color: string
  let percentage: number

  switch (score) {
    case 0:
      label = 'Très faible'
      color = '#ef4444' // red-500
      percentage = 20
      break
    case 1:
      label = 'Faible'
      color = '#f59e0b' // amber-500
      percentage = 40
      break
    case 2:
      label = 'Moyen'
      color = '#eab308' // yellow-500
      percentage = 60
      break
    case 3:
      label = 'Fort'
      color = '#22c55e' // green-500
      percentage = 80
      break
    case 4:
      label = 'Très fort'
      color = '#10b981' // emerald-500
      percentage = 100
      break
    default:
      label = 'Très faible'
      color = '#ef4444'
      percentage = 20
  }

  return {
    score,
    label,
    color,
    percentage,
    requirements,
  }
}

/**
 * LOT-MDP-8 : validité = la règle produit unique, 8 caractères minimum.
 * Les exigences de composition (majuscule/chiffre/spécial) ne sont PLUS
 * bloquantes — elles n'alimentent que la jauge de force indicative.
 */
export function isPasswordValid(password: string): boolean {
  return password.length >= PASSWORD_MIN_LENGTH
}

export function getPasswordErrors(password: string): string[] {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return ['Le mot de passe doit contenir au moins 8 caractères']
  }
  return []
}
